import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ApartmentOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { Button, Card, Empty, Tag } from "antd";
import Heading from "../../components/Heading";
import "../SearchResults/shared.scss";
import "./style.scss";

const getReadableValue = (value) => {
  if (value == null) return "";
  const normalized = String(value).trim();
  if (!normalized || normalized === "0" || normalized.toLowerCase() === "null") {
    return "";
  }
  return normalized;
};

const mergeValues = (...values) => values.filter(Boolean).join(" / ");

const CPSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState([]);

  useEffect(() => {
    const { cpData } = location.state || {};
    setSearchData(Array.isArray(cpData) ? cpData : []);
  }, [location.state]);

  const searchTerm = location.state?.searchTerm || "";
  const resultCount = searchData.length;
  const uniqueHubCount = new Set(
    searchData
      .map((item) => {
        const hubDetails = item?.hubDetails || {};
        return getReadableValue(hubDetails?.hubId) || getReadableValue(hubDetails?.name);
      })
      .filter(Boolean)
  ).size;
  const mappedPincodeCount = searchData.reduce((count, item) => {
    const pincodeDetails = Array.isArray(item?.pincodeDetails)
      ? item.pincodeDetails
      : [];
    return count + pincodeDetails.length;
  }, 0);

  const goBackToPrevScreen = () => {
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="global-search-page cp-search-page">
      <div className="p-2-tab">
        <Heading
          title="CP Search Results"
          showBack={true}
          goBack={goBackToPrevScreen}
        />
      </div>

      <div className="global-search-shell">
        <Card className="global-search-panel global-search-header-card" bordered={false}>
          <div className="global-search-title-row">
            <div className="global-search-title-block">
              <h1>{searchTerm || "CP Search Results"}</h1>
            </div>
            {resultCount ? (
              <span className="global-search-badge">{resultCount} matches</span>
            ) : null}
          </div>
          <div className="global-search-actions">
            <Button onClick={goBackToPrevScreen}>Back</Button>
          </div>
        </Card>

        <div className="global-search-metric-grid">
          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Results</div>
            <div className="global-search-metric-value">{resultCount}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Linked Hubs</div>
            <div className="global-search-metric-value">{uniqueHubCount}</div>
          </Card>

          <Card className="global-search-metric" bordered={false}>
            <div className="global-search-metric-label">Coverage Pins</div>
            <div className="global-search-metric-value">{mappedPincodeCount}</div>
          </Card>
        </div>

        {searchData.length ? (
          <div className="global-search-result-grid">
            {searchData.map((item, index) => {
              const cpDetails = item?.cpDetails || {};
              const hubDetails = item?.hubDetails || {};
              const pincodeDetails = Array.isArray(item?.pincodeDetails)
                ? item.pincodeDetails
                : [];

              const cpName = getReadableValue(cpDetails?.cpName);
              const cpType = getReadableValue(cpDetails?.cpType);
              const cpAddress = getReadableValue(cpDetails?.address);
              const cpOfficialNumber = getReadableValue(cpDetails?.officialNumber);
              const cpPersonalNumber = getReadableValue(cpDetails?.personalNumber);
              const cpEmail = getReadableValue(cpDetails?.officialEmailId);
              const cpPincode = getReadableValue(cpDetails?.pincode);

              const hubName = getReadableValue(hubDetails?.name);
              const hubAddress = getReadableValue(hubDetails?.address);
              const hubOfficialNumber = getReadableValue(hubDetails?.officialNumber);
              const hubPersonalNumber = getReadableValue(hubDetails?.personalNumber);
              const hubEmail = getReadableValue(hubDetails?.email);
              const hubPincode = getReadableValue(hubDetails?.pincode);

              const cpPhone = mergeValues(cpOfficialNumber, cpPersonalNumber);
              const hubPhone = mergeValues(hubOfficialNumber, hubPersonalNumber);
              const callNumber =
                cpOfficialNumber || cpPersonalNumber || hubOfficialNumber || hubPersonalNumber;
              const emailAddress = cpEmail || hubEmail;

              const coveragePins = pincodeDetails
                .map((detail) => ({
                  pincode: getReadableValue(detail?.pincode),
                  area: getReadableValue(detail?.servicableArea),
                }))
                .filter((detail) => detail.pincode);
              const mapPincode = cpPincode || hubPincode || coveragePins[0]?.pincode || "";

              const cpMeta = [
                { label: "Type", value: cpType },
                { label: "Address", value: cpAddress },
                { label: "Phone", value: cpPhone },
                { label: "Email", value: cpEmail },
                { label: "Pincode", value: cpPincode },
              ].filter((metaItem) => metaItem.value);
              const hubMeta = [
                { label: "Hub", value: hubName },
                { label: "Address", value: hubAddress },
                { label: "Phone", value: hubPhone },
                { label: "Email", value: hubEmail },
                { label: "Pincode", value: hubPincode },
              ].filter((metaItem) => metaItem.value);

              const subtitleParts = [];
              if (cpType) subtitleParts.push(cpType);
              if (hubName) subtitleParts.push(`Hub: ${hubName}`);
              const subtitle = subtitleParts.join(" | ");

              return (
                <Card
                  key={`${cpDetails?.cpId || cpName || "cp"}-${index}`}
                  className="global-search-result-card cp-result-card"
                  bordered={false}
                >
                  <div className="global-search-card-head">
                    <div>
                      <h2 className="global-search-card-title">
                        {cpName || `CP Result ${index + 1}`}
                      </h2>
                      {subtitle ? (
                        <p className="global-search-card-subtitle">{subtitle}</p>
                      ) : null}
                    </div>
                    {coveragePins.length ? (
                      <Tag className="cp-result-badge">
                        {coveragePins.length} pin{coveragePins.length === 1 ? "" : "s"}
                      </Tag>
                    ) : null}
                  </div>

                  <div className="global-search-detail-grid">
                    {cpMeta.length ? (
                      <div className="global-search-detail-panel">
                        <h4>
                          <ShopOutlined /> CP
                        </h4>
                        <div className="global-search-meta-list">
                          {cpMeta.map((metaItem) => (
                            <div className="global-search-meta-item" key={`cp-${metaItem.label}`}>
                              <div className="global-search-meta-label">{metaItem.label}</div>
                              <div className="global-search-meta-value">{metaItem.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {hubMeta.length ? (
                      <div className="global-search-detail-panel">
                        <h4>
                          <ApartmentOutlined /> Hub
                        </h4>
                        <div className="global-search-meta-list">
                          {hubMeta.map((metaItem) => (
                            <div className="global-search-meta-item" key={`hub-${metaItem.label}`}>
                              <div className="global-search-meta-label">{metaItem.label}</div>
                              <div className="global-search-meta-value">{metaItem.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {coveragePins.length ? (
                    <div className="global-search-chip-row">
                      {coveragePins.slice(0, 8).map((detail, detailIndex) => (
                        <span
                          key={`${detail.pincode}-${detailIndex}`}
                          className="global-search-chip"
                        >
                          {detail.pincode}
                          {detail.area ? <span>{detail.area}</span> : null}
                        </span>
                      ))}
                      {coveragePins.length > 8 ? (
                        <span className="global-search-chip">+{coveragePins.length - 8} more</span>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="global-search-inline-links">
                    {callNumber ? (
                      <Button href={`tel:${callNumber}`}>
                        <PhoneOutlined /> Call
                      </Button>
                    ) : null}
                    {emailAddress ? (
                      <Button href={`mailto:${emailAddress}`}>
                        <MailOutlined /> Email
                      </Button>
                    ) : null}
                    {mapPincode ? (
                      <Button
                        onClick={() =>
                          navigate(`/pincode-map?pincode=${encodeURIComponent(mapPincode)}`)
                        }
                      >
                        <EnvironmentOutlined /> Open Pincode Map
                      </Button>
                    ) : null}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="global-search-panel global-search-empty" bordered={false}>
            <Empty
              description={
                searchTerm
                  ? `No CP results were returned for "${searchTerm}".`
                  : "Run a CP or hub lookup from global search to view results."
              }
            />
            <div className="global-search-actions global-search-actions-tight">
              <Button onClick={goBackToPrevScreen}>Back</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CPSearch;
